import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  Chip
} from '@mui/material';
import { Build as ServiceIcon } from '@mui/icons-material';
import { useMsal } from '@azure/msal-react';

const ServiceAssignDialog = ({ 
  open, 
  onClose, 
  onAssign, 
  employees, 
  selectedDate, 
  existingService 
}) => {
  const { accounts } = useMsal();
  const [selectedEmployee, setSelectedEmployee] = useState(existingService?.employee_code || '');
  const [customText, setCustomText] = useState(existingService?.custom_text || '');

  const getCurrentUserEmail = () => {
    if (accounts && accounts.length > 0) {
      return accounts[0].username || accounts[0].email || 'user';
    }
    return 'user';
  };

  const handleAssign = () => {
    if (!selectedEmployee) {
      return;
    }

    const serviceData = {
      employee_code: selectedEmployee,
      year: selectedDate.getFullYear(),
      month: selectedDate.getMonth() + 1,
      day: selectedDate.getDate(),
      custom_text: customText,
      updated_by: getCurrentUserEmail()
    };

    onAssign(serviceData);
    handleClose();
  };

  const handleClose = () => {
    setSelectedEmployee(existingService?.employee_code || '');
    setCustomText(existingService?.custom_text || '');
    onClose();
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('pl-PL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const selectedEmployeeName = employees.find(emp => emp.code === selectedEmployee)?.name || selectedEmployee;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ServiceIcon color="primary" />
          <Typography variant="h6">
            {existingService ? 'Zmień serwis' : 'Przypisz serwis'}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {/* Data serwisu */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Data serwisu:
            </Typography>
            <Chip 
              label={formatDate(selectedDate)}
              color="primary"
              variant="outlined"
              sx={{ fontSize: '0.9rem' }}
            />
          </Box>

          {/* Aktualny serwis (jeśli istnieje) */}
          {existingService && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Aktualny serwis:
              </Typography>
              <Typography variant="body2">
                <strong>{existingService.employee_code}</strong>
                {existingService.custom_text && ` - ${existingService.custom_text}`}
              </Typography>
            </Box>
          )}

          {/* Wybór pracownika */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Pracownik</InputLabel>
            <Select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              label="Pracownik"
            >
              {employees
                .filter(emp => emp.is_active)
                .map((employee) => (
                  <MenuItem key={employee.code} value={employee.code}>
                    <Box display="flex" justifyContent="space-between" width="100%">
                      <span><strong>{employee.code}</strong></span>
                      <span style={{ color: 'text.secondary' }}>
                        {employee.name || 'Brak nazwy'}
                      </span>
                    </Box>
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* Opis serwisu */}
          <TextField
            label="Opis serwisu (opcjonalnie)"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="np. Konserwacja sprzętu, Dyżur techniczny..."
            helperText="Dodatkowe informacje o serwisie"
          />

          {/* Podgląd */}
          {selectedEmployee && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Podgląd przypisania:
              </Typography>
              <Typography variant="body2">
                <ServiceIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                <strong>{selectedEmployeeName}</strong> będzie miał serwis 
                dnia <strong>{formatDate(selectedDate)}</strong>
                {customText && (
                  <>
                    <br />
                    <em>"{customText}"</em>
                  </>
                )}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>
          Anuluj
        </Button>
        <Button 
          onClick={handleAssign}
          variant="contained"
          disabled={!selectedEmployee}
          startIcon={<ServiceIcon />}
        >
          {existingService ? 'Zmień serwis' : 'Przypisz serwis'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceAssignDialog;